import { loadMapData } from './data-loader';
import { createColorScale, NO_DATA_COLOR, type Bin } from './color-scale';
import { createMapRenderer, type MapRenderer } from './map-renderer';
import { formatHdiTooltip, formatWhrTooltip, formatOecdTooltip } from './tooltip';
import { searchRegions, buildSearchIndex, type SearchIndex } from './region-search';
import { PALETTES, DEFAULT_PALETTE_ID, getPaletteById, type PaletteId } from './palette-registry';
import { getRegionSource } from './region-supplements';
import { createValueLoader } from './value-loader';
import { getIndexById, INDICES, type IndexId, type IndexDefinition } from './index-registry';
import { createGetValue } from './get-value-accessor';
import { EQUAL_WEIGHTS, redistributeWeights, normalizeWeights, type DimensionWeights } from './weighted-average';
import type { HdiValues } from './schemas/hdi-values.schema';
import type { WhrValues } from './schemas/whr-values.schema';
import type { OecdBliValues } from './schemas/oecd-bli-values.schema';
import type { RegionProperties } from './schemas/region-properties.schema';
import { parseUrlHash, toUrlHash } from './url-state';

const DATA_URL = `${import.meta.env.BASE_URL}data/regions.topo.json`;

const createTooltipController = (container: HTMLElement) => {
  const tooltip = document.createElement('div');
  tooltip.className = 'tooltip';
  tooltip.style.cssText =
    'position:absolute;pointer-events:none;opacity:0;transition:opacity 0.15s;' +
    'background:rgba(10,10,46,0.92);color:#e0e0e0;padding:10px 14px;border-radius:6px;' +
    'font-size:13px;line-height:1.5;max-width:280px;z-index:100;border:1px solid rgba(255,255,255,0.1);' +
    'box-shadow:0 4px 12px rgba(0,0,0,0.4)';
  container.appendChild(tooltip);

  return {
    show: (html: string, x: number, y: number): void => {
      tooltip.innerHTML = html;
      tooltip.style.opacity = '1';

      const containerRect = container.getBoundingClientRect();
      const tooltipWidth = tooltip.offsetWidth;
      const tooltipHeight = tooltip.offsetHeight;

      const left = x + 15 + tooltipWidth > containerRect.width
        ? x - tooltipWidth - 10
        : x + 15;
      const top = y + 15 + tooltipHeight > containerRect.height
        ? y - tooltipHeight - 10
        : y + 15;

      tooltip.style.left = `${left}px`;
      tooltip.style.top = `${top}px`;
    },
    hide: (): void => {
      tooltip.style.opacity = '0';
    },
  };
};

const createLegend = (
  container: HTMLElement,
  legendTitle: string,
  bins: readonly Bin[],
  onBinHover: (filter: { min: number; max: number } | null) => void
): HTMLElement => {
  const legend = document.createElement('div');
  legend.className = 'legend';
  legend.style.cssText =
    'position:absolute;bottom:24px;left:24px;background:rgba(10,10,46,0.85);' +
    'padding:12px 16px;border-radius:8px;font-size:12px;color:#ccc;' +
    'border:1px solid rgba(255,255,255,0.1);z-index:50';

  const title = document.createElement('div');
  title.textContent = legendTitle;
  title.style.cssText = 'font-weight:600;margin-bottom:8px;color:#e0e0e0;font-size:13px';
  legend.appendChild(title);

  bins.forEach((bin) => {
    const row = document.createElement('div');
    row.style.cssText = 'display:flex;align-items:center;gap:8px;padding:3px 0;cursor:pointer;border-radius:3px';

    const swatch = document.createElement('div');
    swatch.style.cssText = `width:16px;height:12px;background:${bin.color};border-radius:2px;flex-shrink:0`;

    const label = document.createElement('span');
    label.textContent = bin.label;

    row.appendChild(swatch);
    row.appendChild(label);

    row.addEventListener('mouseenter', () => {
      onBinHover({ min: bin.min, max: bin.max });
      row.style.background = 'rgba(255,255,255,0.08)';
    });
    row.addEventListener('mouseleave', () => {
      onBinHover(null);
      row.style.background = '';
    });

    legend.appendChild(row);
  });

  const noDataRow = document.createElement('div');
  noDataRow.style.cssText = 'display:flex;align-items:center;gap:8px;padding:3px 0;margin-top:4px;border-top:1px solid rgba(255,255,255,0.1);padding-top:6px';
  const noDataSwatch = document.createElement('div');
  noDataSwatch.style.cssText = `width:16px;height:12px;background:${NO_DATA_COLOR};border-radius:2px;flex-shrink:0`;
  const noDataLabel = document.createElement('span');
  noDataLabel.textContent = 'No data';
  noDataRow.appendChild(noDataSwatch);
  noDataRow.appendChild(noDataLabel);
  legend.appendChild(noDataRow);

  container.appendChild(legend);
  return legend;
};

const createSearchUI = (
  container: HTMLElement,
  index: SearchIndex,
  onSelect: (gdlCode: string) => void
): void => {
  const wrapper = document.createElement('div');
  wrapper.style.cssText =
    'position:absolute;top:16px;right:16px;z-index:50;width:260px';

  const input = document.createElement('input');
  input.type = 'text';
  input.placeholder = 'Search regions...';
  input.style.cssText =
    'width:100%;padding:8px 12px;background:rgba(10,10,46,0.9);color:#e0e0e0;' +
    'border:1px solid rgba(255,255,255,0.15);border-radius:6px;font-size:14px;' +
    'outline:none;box-sizing:border-box';

  const dropdown = document.createElement('ul');
  dropdown.style.cssText =
    'list-style:none;margin:4px 0 0;padding:0;background:rgba(10,10,46,0.95);' +
    'border:1px solid rgba(255,255,255,0.15);border-radius:6px;max-height:300px;' +
    'overflow-y:auto;display:none';

  let debounceTimer: ReturnType<typeof setTimeout>;

  input.addEventListener('input', () => {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      const query = input.value;
      const results = searchRegions({ query, index });

      dropdown.innerHTML = '';

      if (query.trim() === '') {
        dropdown.style.display = 'none';
        return;
      }

      if (results.length === 0) {
        const li = document.createElement('li');
        li.textContent = 'No regions found';
        li.style.cssText = 'padding:8px 12px;color:#888;font-style:italic;font-size:13px';
        dropdown.appendChild(li);
      } else {
        results.forEach((r) => {
          const li = document.createElement('li');
          li.textContent = r.label;
          li.style.cssText =
            'padding:8px 12px;cursor:pointer;font-size:13px;color:#ccc;border-bottom:1px solid rgba(255,255,255,0.05)';
          li.addEventListener('mouseenter', () => {
            li.style.background = 'rgba(255,255,255,0.08)';
          });
          li.addEventListener('mouseleave', () => {
            li.style.background = '';
          });
          li.addEventListener('click', () => {
            onSelect(r.gdlCode);
            input.value = r.label;
            dropdown.style.display = 'none';
          });
          dropdown.appendChild(li);
        });
      }

      dropdown.style.display = 'block';
    }, 50);
  });

  input.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      dropdown.style.display = 'none';
      input.blur();
    }
  });

  document.addEventListener('click', (e) => {
    if (!wrapper.contains(e.target as Node)) {
      dropdown.style.display = 'none';
    }
  });

  document.addEventListener('keydown', (e) => {
    if ((e.key === '/' || (e.ctrlKey && e.key === 'k')) && document.activeElement !== input) {
      e.preventDefault();
      input.focus();
    }
  });

  wrapper.appendChild(input);
  wrapper.appendChild(dropdown);
  container.appendChild(wrapper);
};

const createPalettePicker = (
  container: HTMLElement,
  onChange: (paletteId: PaletteId) => void
): void => {
  const wrapper = document.createElement('div');
  wrapper.style.cssText =
    'position:absolute;top:88px;left:16px;z-index:50';

  const select = document.createElement('select');
  select.style.cssText =
    'padding:6px 10px;background:rgba(10,10,46,0.9);color:#e0e0e0;' +
    'border:1px solid rgba(255,255,255,0.15);border-radius:6px;font-size:13px;' +
    'cursor:pointer;outline:none';

  PALETTES.forEach((p) => {
    const option = document.createElement('option');
    option.value = p.id;
    option.textContent = p.label;
    option.selected = p.id === DEFAULT_PALETTE_ID;
    select.appendChild(option);
  });

  select.addEventListener('change', () => {
    onChange(select.value as PaletteId);
  });

  wrapper.appendChild(select);
  container.appendChild(wrapper);
};

const createIndexSwitcher = (
  container: HTMLElement,
  currentIndexId: IndexId,
  onChange: (indexId: IndexId) => void
): void => {
  const wrapper = document.createElement('div');
  wrapper.style.cssText =
    'position:absolute;top:16px;left:16px;z-index:50';

  const select = document.createElement('select');
  select.style.cssText =
    'padding:6px 10px;background:rgba(10,10,46,0.9);color:#e0e0e0;' +
    'border:1px solid rgba(255,255,255,0.15);border-radius:6px;font-size:13px;' +
    'cursor:pointer;outline:none';

  INDICES.forEach((idx) => {
    const option = document.createElement('option');
    option.value = idx.id;
    option.textContent = idx.label;
    option.selected = idx.id === currentIndexId;
    select.appendChild(option);
  });

  select.addEventListener('change', () => {
    onChange(select.value as IndexId);
  });

  wrapper.appendChild(select);
  container.appendChild(wrapper);
};

type DimensionSelector = {
  readonly show: (dimensions: readonly { id: string; label: string }[]) => void;
  readonly hide: () => void;
};

const createDimensionSelector = (
  container: HTMLElement,
  onChange: (dimensionId: string) => void
): DimensionSelector => {
  const wrapper = document.createElement('div');
  wrapper.style.cssText =
    'position:absolute;top:52px;left:16px;z-index:50;display:none';

  const select = document.createElement('select');
  select.style.cssText =
    'padding:6px 10px;background:rgba(10,10,46,0.9);color:#e0e0e0;' +
    'border:1px solid rgba(255,255,255,0.15);border-radius:6px;font-size:13px;' +
    'cursor:pointer;outline:none';

  select.addEventListener('change', () => {
    onChange(select.value);
  });

  wrapper.appendChild(select);
  container.appendChild(wrapper);

  return {
    show: (dimensions) => {
      select.innerHTML = '';
      dimensions.forEach((dim) => {
        const option = document.createElement('option');
        option.value = dim.id;
        option.textContent = dim.label;
        select.appendChild(option);
      });
      wrapper.style.display = 'block';
    },
    hide: () => {
      wrapper.style.display = 'none';
    },
  };
};

type WeightSliderPanel = {
  readonly show: () => void;
  readonly hide: () => void;
  readonly getWeights: () => DimensionWeights;
};

const SLIDER_LABELS: readonly { key: keyof DimensionWeights; label: string }[] = [
  { key: 'income', label: 'Income' },
  { key: 'jobs', label: 'Jobs' },
  { key: 'housing', label: 'Housing' },
  { key: 'education', label: 'Education' },
  { key: 'health', label: 'Health' },
  { key: 'environment', label: 'Environment' },
  { key: 'safety', label: 'Safety' },
  { key: 'civicEngagement', label: 'Civic Eng.' },
  { key: 'accessToServices', label: 'Access Svc.' },
  { key: 'community', label: 'Community' },
  { key: 'lifeSatisfaction', label: 'Life Sat.' },
];

const createWeightSliders = (
  container: HTMLElement,
  onChange: (weights: DimensionWeights) => void
): WeightSliderPanel => {
  const wrapper = document.createElement('div');
  wrapper.style.cssText =
    'position:absolute;top:124px;left:16px;z-index:50;display:none;' +
    'background:rgba(10,10,46,0.9);border:1px solid rgba(255,255,255,0.15);' +
    'border-radius:8px;padding:10px 14px;width:220px;max-height:400px;overflow-y:auto';

  let currentPercentages = Object.fromEntries(
    SLIDER_LABELS.map(({ key }) => [key, 100 / 11])
  ) as Record<keyof DimensionWeights, number>;

  const sliderElements: Record<string, { slider: HTMLInputElement; label: HTMLSpanElement }> = {};

  const updateDisplay = (): void => {
    for (const { key } of SLIDER_LABELS) {
      const el = sliderElements[key];
      if (el) {
        el.slider.value = String(Math.round(currentPercentages[key]));
        el.label.textContent = `${Math.round(currentPercentages[key])}%`;
      }
    }
  };

  let debounceTimer: ReturnType<typeof setTimeout>;

  const handleSliderChange = (changedKey: keyof DimensionWeights, rawValue: number): void => {
    const currentWeightsNormalized = normalizeWeights(
      Object.fromEntries(
        SLIDER_LABELS.map(({ key }) => [key, currentPercentages[key]])
      ) as unknown as DimensionWeights
    );

    const result = redistributeWeights({
      currentWeights: currentWeightsNormalized,
      changedKey,
      newPercentage: rawValue,
    });

    currentPercentages = { ...result } as Record<keyof DimensionWeights, number>;
    updateDisplay();

    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      const normalized = normalizeWeights(
        Object.fromEntries(
          SLIDER_LABELS.map(({ key }) => [key, currentPercentages[key]])
        ) as unknown as DimensionWeights
      );
      onChange(normalized);
    }, 80);
  };

  SLIDER_LABELS.forEach(({ key, label }) => {
    const row = document.createElement('div');
    row.style.cssText = 'display:flex;align-items:center;gap:6px;margin-bottom:4px;font-size:11px;color:#ccc';

    const labelEl = document.createElement('span');
    labelEl.textContent = label;
    labelEl.style.cssText = 'width:64px;flex-shrink:0;text-align:right';

    const slider = document.createElement('input');
    slider.type = 'range';
    slider.min = '0';
    slider.max = '100';
    slider.value = String(Math.round(100 / 11));
    slider.style.cssText = 'flex:1;height:4px;cursor:pointer;accent-color:#cc4778';

    const pctLabel = document.createElement('span');
    pctLabel.textContent = `${Math.round(100 / 11)}%`;
    pctLabel.style.cssText = 'width:30px;text-align:right;font-size:10px;color:#888';

    slider.addEventListener('input', () => {
      handleSliderChange(key, Number(slider.value));
    });

    sliderElements[key] = { slider, label: pctLabel };

    row.appendChild(labelEl);
    row.appendChild(slider);
    row.appendChild(pctLabel);
    wrapper.appendChild(row);
  });

  const resetBtn = document.createElement('button');
  resetBtn.textContent = 'Reset';
  resetBtn.style.cssText =
    'width:100%;margin-top:6px;padding:4px;background:rgba(255,255,255,0.08);' +
    'color:#ccc;border:1px solid rgba(255,255,255,0.15);border-radius:4px;' +
    'font-size:11px;cursor:pointer';
  resetBtn.addEventListener('click', () => {
    currentPercentages = Object.fromEntries(
      SLIDER_LABELS.map(({ key }) => [key, 100 / 11])
    ) as Record<keyof DimensionWeights, number>;
    updateDisplay();
    onChange(EQUAL_WEIGHTS);
  });
  wrapper.appendChild(resetBtn);

  container.appendChild(wrapper);

  return {
    show: () => { wrapper.style.display = 'block'; },
    hide: () => { wrapper.style.display = 'none'; },
    getWeights: () => normalizeWeights(
      Object.fromEntries(
        SLIDER_LABELS.map(({ key }) => [key, currentPercentages[key]])
      ) as unknown as DimensionWeights
    ),
  };
};

const formatGenericTooltip = (
  properties: RegionProperties,
  value: number | null,
  indexDef: IndexDefinition,
  source?: string
): string => {
  const titleHtml = properties.level === 'national' || properties.name === properties.country
    ? `<div><strong>${properties.name}</strong></div>`
    : `<div><strong>${properties.name}</strong>, ${properties.country}</div>`;

  const valueHtml = value !== null
    ? `<div>${indexDef.label}: ${value.toFixed(1)}</div>`
    : '<div>No data available</div>';

  const noteHtml = properties.level === 'national'
    ? '<div class="tooltip-note">Country-level data</div>'
    : '';

  const sourceHtml = source ? `<div class="tooltip-source">Source: ${source}</div>` : '';

  return [titleHtml, valueHtml, noteHtml, sourceHtml].filter(Boolean).join('');
};

const createInfoPanel = (container: HTMLElement): void => {
  const overlay = document.createElement('div');
  overlay.className = 'info-overlay';
  overlay.style.cssText =
    'display:none;position:absolute;inset:0;background:rgba(0,0,0,0.5);z-index:200;' +
    'align-items:center;justify-content:center';

  const panel = document.createElement('div');
  panel.className = 'info-panel';
  panel.style.cssText =
    'background:rgba(10,10,46,0.95);color:#e0e0e0;padding:24px 28px;border-radius:10px;' +
    'max-width:420px;width:90%;border:1px solid rgba(255,255,255,0.15);' +
    'box-shadow:0 8px 32px rgba(0,0,0,0.6);font-size:13px;line-height:1.6';

  const closeBtn = document.createElement('button');
  closeBtn.textContent = '\u00d7';
  closeBtn.style.cssText =
    'float:right;background:none;border:none;color:#888;font-size:22px;cursor:pointer;' +
    'padding:0 0 0 12px;line-height:1';

  const title = document.createElement('h3');
  title.textContent = 'Data Sources';
  title.style.cssText = 'margin:0 0 14px;font-size:15px;color:#fff';

  const content = document.createElement('div');
  content.innerHTML = [
    '<div style="margin-bottom:10px"><strong>HDI Data</strong><br>Global Data Lab, Subnational HDI v8.3<br>' +
      '<span style="color:#888">Zenodo DOI: 10.5281/zenodo.17467221</span></div>',
    '<div style="margin-bottom:10px"><strong>World Happiness Report</strong><br>' +
      'Helliwell et al. (2025), <em>World Happiness Report 2025</em><br>' +
      '<span style="color:#888">Country-level Cantril Ladder scores</span></div>',
    '<div style="margin-bottom:10px"><strong>OECD Better Life Index</strong><br>' +
      'OECD Regional Well-Being<br>' +
      '<span style="color:#888">Licensed under CC BY 4.0</span></div>',
    '<div style="margin-bottom:10px"><strong>Boundaries</strong><br>GDL Shapefiles V6.5</div>',
    '<div style="margin-bottom:10px"><strong>Supplemental</strong><br>' +
      'Taiwan: DGBAS (Statistics Bureau)<br>' +
      'Hong Kong, San Marino: UNDP HDR<br>' +
      '<span style="color:#888">HDI calculated per UNDP methodology</span></div>',
    '<div style="margin-bottom:10px"><strong>Projection</strong><br>Robinson (via d3-geo-projection)</div>',
    '<div style="border-top:1px solid rgba(255,255,255,0.1);padding-top:10px;margin-top:10px;color:#888;font-size:12px">' +
      'HDI data for non-commercial use. Attribution: Global Data Lab.<br>' +
      'OECD data licensed under CC BY 4.0.</div>',
  ].join('');

  panel.appendChild(closeBtn);
  panel.appendChild(title);
  panel.appendChild(content);
  overlay.appendChild(panel);
  container.appendChild(overlay);

  const button = document.createElement('button');
  button.className = 'info-button';
  button.textContent = 'i';
  button.style.cssText =
    'position:absolute;bottom:24px;right:24px;z-index:50;width:32px;height:32px;' +
    'border-radius:50%;background:rgba(10,10,46,0.85);color:#e0e0e0;border:1px solid rgba(255,255,255,0.15);' +
    'font-size:16px;font-style:italic;font-family:Georgia,serif;cursor:pointer;' +
    'display:flex;align-items:center;justify-content:center';
  container.appendChild(button);

  const toggleOverlay = (show: boolean): void => {
    overlay.style.display = show ? 'flex' : 'none';
  };

  button.addEventListener('click', () => toggleOverlay(true));
  closeBtn.addEventListener('click', () => toggleOverlay(false));
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) toggleOverlay(false);
  });
};

const showLoadingState = (container: HTMLElement): HTMLElement => {
  const loading = document.createElement('div');
  loading.className = 'loading-state';
  loading.style.cssText =
    'display:flex;flex-direction:column;align-items:center;justify-content:center;' +
    'height:100%;color:#888;font-size:16px;gap:12px';
  loading.innerHTML = `
    <div style="width:40px;height:40px;border:3px solid rgba(255,255,255,0.1);
      border-top-color:#cc4778;border-radius:50%;animation:spin 0.8s linear infinite"></div>
    <span>Loading map data...</span>
  `;

  const style = document.createElement('style');
  style.textContent = '@keyframes spin { to { transform: rotate(360deg); } }';
  document.head.appendChild(style);

  container.appendChild(loading);
  return loading;
};

const showErrorState = (container: HTMLElement, message: string): void => {
  const error = document.createElement('div');
  error.className = 'error-state';
  error.style.cssText =
    'display:flex;flex-direction:column;align-items:center;justify-content:center;' +
    'height:100%;color:#e0e0e0;font-size:16px;gap:12px;text-align:center;padding:2rem';
  error.innerHTML = `
    <div style="font-size:2rem">!</div>
    <h2 style="margin:0;font-size:1.2rem">Failed to load map</h2>
    <p style="color:#888;max-width:400px">${message}</p>
    <button onclick="location.reload()" style="padding:8px 20px;background:#cc4778;color:#fff;
      border:none;border-radius:6px;cursor:pointer;font-size:14px">Retry</button>
  `;
  container.appendChild(error);
};

const createLoadingOverlay = (container: HTMLElement) => {
  const overlay = document.createElement('div');
  overlay.style.cssText =
    'display:none;position:absolute;inset:0;background:rgba(0,0,0,0.3);z-index:150;' +
    'align-items:center;justify-content:center';
  overlay.innerHTML =
    '<div style="width:32px;height:32px;border:3px solid rgba(255,255,255,0.15);' +
    'border-top-color:#cc4778;border-radius:50%;animation:spin 0.8s linear infinite"></div>';
  container.appendChild(overlay);

  return {
    show: (): void => { overlay.style.display = 'flex'; },
    hide: (): void => { overlay.style.display = 'none'; },
  };
};

const showToast = (container: HTMLElement, message: string): void => {
  const toast = document.createElement('div');
  toast.style.cssText =
    'position:absolute;bottom:60px;left:50%;transform:translateX(-50%);z-index:200;' +
    'background:rgba(180,40,40,0.92);color:#fff;padding:10px 20px;border-radius:6px;' +
    'font-size:13px;max-width:400px;text-align:center;opacity:1;transition:opacity 0.3s';
  toast.textContent = message;
  container.appendChild(toast);
  setTimeout(() => {
    toast.style.opacity = '0';
    setTimeout(() => toast.remove(), 300);
  }, 5000);
};

export const initApp = async (container: HTMLElement): Promise<void> => {
  const mapContainer = container.querySelector<HTMLElement>('#map-container');
  if (!mapContainer) return;

  mapContainer.style.cssText = 'position:relative;width:100%;height:100%';

  const loading = showLoadingState(mapContainer);

  try {
    const valueLoader = createValueLoader();
    const initialUrlState = parseUrlHash(window.location.hash);
    const initialIndexId = initialUrlState.indexId;

    let currentIndexDef = getIndexById(initialIndexId);
    let currentPaletteId = DEFAULT_PALETTE_ID;
    let currentDimensionId: string | undefined = initialUrlState.dimensionId;

    const [{ regions }, initialValues] = await Promise.all([
      loadMapData(DATA_URL),
      valueLoader.loadValues(initialIndexId),
    ]);

    loading.remove();

    const switchOverlay = createLoadingOverlay(mapContainer);
    let loadGeneration = 0;

    const updateUrlHash = (): void => {
      const hash = toUrlHash({ indexId: currentIndexDef.id, dimensionId: currentDimensionId });
      history.replaceState(null, '', hash);
      document.title = `Global Well-Being Map \u2014 ${currentIndexDef.label}`;
    };

    let currentGetValue = createGetValue({
      indexId: initialIndexId,
      values: initialValues,
      dimensionId: currentDimensionId,
    });

    let currentScale = createColorScale({
      interpolator: getPaletteById(currentPaletteId).interpolator,
      binDefinitions: currentIndexDef.binDefinitions,
    });

    const tooltipController = createTooltipController(mapContainer);

    const formatTooltip = (properties: RegionProperties, gdlCode: string): string => {
      const source = getRegionSource(gdlCode);

      if (currentIndexDef.id === 'hdi') {
        const hdiValues = valueLoader.getCachedValues('hdi') as HdiValues | null;
        return formatHdiTooltip({
          properties,
          hdiValue: hdiValues?.[gdlCode],
          source,
        });
      }

      if (currentIndexDef.id === 'whr') {
        const whrValues = valueLoader.getCachedValues('whr') as WhrValues | null;
        return formatWhrTooltip({
          properties,
          whrValue: whrValues?.[properties.countryIso],
          source,
        });
      }

      if (currentIndexDef.id === 'oecd-bli') {
        const oecdValues = valueLoader.getCachedValues('oecd-bli') as OecdBliValues | null;
        const oecdValue = oecdValues?.[properties.countryIso];
        const value = currentGetValue(gdlCode, properties.countryIso);
        const dimensionDef = currentDimensionId
          ? currentIndexDef.dimensions?.find((d) => d.id === currentDimensionId)
          : undefined;

        return formatOecdTooltip({
          properties,
          oecdValue,
          dimensionLabel: dimensionDef?.label,
          compositeScore: !dimensionDef && value !== null ? value : undefined,
          source,
        });
      }

      const value = currentGetValue(gdlCode, properties.countryIso);
      return formatGenericTooltip(properties, value, currentIndexDef, source);
    };

    const renderer: MapRenderer = createMapRenderer({
      container: mapContainer,
      regions,
      getColor: currentScale.getColor,
      getValue: currentGetValue,
      onRegionHover: (feature, event) => {
        if (feature) {
          const rect = mapContainer.getBoundingClientRect();
          const gdlCode = feature.properties.gdlCode;
          tooltipController.show(
            formatTooltip(feature.properties, gdlCode),
            event.clientX - rect.left,
            event.clientY - rect.top
          );
        } else {
          tooltipController.hide();
        }
      },
    });

    renderer.render();

    const onBinHover = (filter: { min: number; max: number } | null): void => {
      renderer.highlightRegions(filter);
    };

    let legendElement = createLegend(mapContainer, currentIndexDef.legendTitle, currentScale.bins, onBinHover);

    const coverageNote = document.createElement('div');
    coverageNote.textContent = 'Data available for OECD member countries only';
    coverageNote.style.cssText =
      'position:absolute;bottom:8px;left:24px;z-index:50;font-size:11px;color:#777;display:none';
    mapContainer.appendChild(coverageNote);

    const updateCoverageNote = (): void => {
      coverageNote.style.display = currentIndexDef.id === 'oecd-bli' ? 'block' : 'none';
    };
    updateCoverageNote();

    const rebuildScale = (): void => {
      currentScale = createColorScale({
        interpolator: getPaletteById(currentPaletteId).interpolator,
        binDefinitions: currentIndexDef.binDefinitions,
      });
      renderer.updateColors(currentScale.getColor);
      legendElement.remove();
      legendElement = createLegend(mapContainer, currentIndexDef.legendTitle, currentScale.bins, onBinHover);
      updateCoverageNote();
    };

    let currentWeights = EQUAL_WEIGHTS;

    const weightSliders = createWeightSliders(mapContainer, (weights) => {
      currentWeights = weights;
      const values = valueLoader.getCachedValues('oecd-bli');
      if (values) {
        currentGetValue = createGetValue({
          indexId: 'oecd-bli',
          values,
          weights: currentWeights,
        });
        renderer.updateValues(currentGetValue);
        rebuildScale();
      }
    });

    const dimensionSelector = createDimensionSelector(mapContainer, (dimensionId) => {
      currentDimensionId = dimensionId === 'weighted-average' ? undefined : dimensionId;
      const values = valueLoader.getCachedValues('oecd-bli');
      if (values) {
        currentGetValue = createGetValue({
          indexId: 'oecd-bli',
          values,
          dimensionId: currentDimensionId,
          weights: currentWeights,
        });
        renderer.updateValues(currentGetValue);
        rebuildScale();
      }
      if (!currentDimensionId) {
        weightSliders.show();
      } else {
        weightSliders.hide();
      }
      updateUrlHash();
    });

    createIndexSwitcher(mapContainer, initialIndexId, async (indexId) => {
      const newIndexDef = getIndexById(indexId);
      const thisGeneration = ++loadGeneration;

      switchOverlay.show();

      try {
        const values = await valueLoader.loadValues(indexId);

        if (thisGeneration !== loadGeneration) return;

        currentIndexDef = newIndexDef;
        currentDimensionId = undefined;
        currentGetValue = createGetValue({ indexId, values, weights: currentWeights });

        renderer.updateValues(currentGetValue);
        rebuildScale();

        if (newIndexDef.dimensions) {
          dimensionSelector.show(newIndexDef.dimensions);
          weightSliders.show();
        } else {
          dimensionSelector.hide();
          weightSliders.hide();
        }
        updateUrlHash();
      } catch (err) {
        if (thisGeneration !== loadGeneration) return;
        showToast(mapContainer, `Failed to load ${newIndexDef.label} data. Try again later.`);
      } finally {
        if (thisGeneration === loadGeneration) {
          switchOverlay.hide();
        }
      }
    });

    createPalettePicker(mapContainer, (paletteId) => {
      currentPaletteId = paletteId;
      rebuildScale();
    });

    const searchableRegions = regions.map((f) => ({
      gdlCode: f.properties.gdlCode,
      name: f.properties.name,
      country: f.properties.country,
    }));
    const searchIndex = buildSearchIndex(searchableRegions);

    createSearchUI(mapContainer, searchIndex, (gdlCode) => {
      renderer.zoomToRegion(gdlCode);
      renderer.highlightSingle(gdlCode);
      setTimeout(() => renderer.highlightSingle(null), 3000);
    });

    createInfoPanel(mapContainer);

    if (currentIndexDef.dimensions) {
      dimensionSelector.show(currentIndexDef.dimensions);
      if (!currentDimensionId) {
        weightSliders.show();
      }
    }

    updateUrlHash();

    window.addEventListener('resize', () => {
      renderer.resize();
    });
  } catch (error) {
    loading.remove();
    showErrorState(
      mapContainer,
      error instanceof Error ? error.message : 'An unexpected error occurred'
    );
  }
};
